import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from "@exhousing/design-system";
import { Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useAddNote, useDeleteNote, useNotes } from "@/hooks/api/useNotes";

function timestamp(value: string): string {
  return new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Reusable authored-remark log for any subject (contract, tenant, landlord, ...). */
export function NotesPanel({
  subjectType,
  subjectId,
  title = "Remarks",
}: {
  subjectType: string;
  subjectId: string;
  title?: string;
}) {
  const { data: notes } = useNotes(subjectType, subjectId);
  const add = useAddNote(subjectType, subjectId);
  const remove = useDeleteNote(subjectType, subjectId);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    add.mutate(
      { body: text, subject: subject.trim() || undefined },
      {
        onSuccess: () => {
          setBody("");
          setSubject("");
        },
        onError: () => toast.error("Could not add the remark"),
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={submit} className="space-y-2">
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject (optional)"
          />
          <div className="flex items-end gap-2">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Add a remark…"
              rows={2}
              className="flex-1 rounded-md border border-input bg-background p-2.5 text-sm outline-none focus:border-primary"
            />
            <Button type="submit" size="sm" disabled={add.isPending || !body.trim()}>
              <Send className="size-4" />
              Add
            </Button>
          </div>
        </form>

        {notes && notes.length > 0 ? (
          <ul className="space-y-3">
            {notes.map((note) => (
              <li
                key={note.id}
                className="group flex items-start justify-between gap-3 border-b border-border/60 pb-3 last:border-0"
              >
                <div>
                  {note.subject && (
                    <p className="text-sm font-semibold">{note.subject}</p>
                  )}
                  <p className="whitespace-pre-line text-sm">{note.body}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {note.author_name ?? "Unknown"} · {timestamp(note.created_at)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    remove.mutate(note.id, {
                      onError: () => toast.error("Could not delete the remark"),
                    })
                  }
                  aria-label="Delete remark"
                  className="shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                >
                  <Trash2 className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No remarks yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
